interface MobileOptimizedStructuredData {
  "@context": string;
  "@type": string;
  [key: string]: unknown;
}

// Generate mobile-optimized structured data for different page types
export const generateMobileStructuredData = {
  // Organization schema with mobile app information
  organization: (): MobileOptimizedStructuredData => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Boho Style Co.",
    "description": "Handcrafted bohemian fashion, jewelry, and lifestyle accessories. Sustainable, artisan-made pieces that embrace your free spirit.",
    "url": "https://bohostyleco.com",
    "logo": "https://bohostyleco.com/lovable-uploads/0a39b4fd-debb-4ee4-9c46-86c84d718d61.png",
    "sameAs": [
      "https://instagram.com/bohostyleco",
      "https://facebook.com/bohostyleco",
      "https://pinterest.com/bohostyleco"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-555-BOHO-STYLE",
      "contactType": "Customer Service",
      "availableLanguage": "English"
    },
    "mobileApplication": {
      "@type": "MobileApplication",
      "name": "Boho Style Co.",
      "operatingSystem": "Progressive Web App",
      "applicationCategory": "Shopping",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "127"
      }
    }
  }),

  // Product schema optimized for mobile shopping
  product: (product: {
    id: string;
    name: string;
    description?: string;
    image?: string;
    price: number;
    availability?: boolean;
    category?: string;
    brand?: string;
  }): MobileOptimizedStructuredData => ({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description || `Beautiful bohemian ${product.name.toLowerCase()} from Boho Style Co.`,
    "image": product.image || "https://bohostyleco.com/og-image.jpg",
    "sku": product.id,
    "brand": {
      "@type": "Brand",
      "name": product.brand || "Boho Style Co."
    },
    "category": product.category || "Fashion Accessories",
    "offers": {
      "@type": "Offer",
      "price": product.price.toString(),
      "priceCurrency": "USD",
      "availability": product.availability !== false 
        ? "https://schema.org/InStock" 
        : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Boho Style Co."
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "USD"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 2,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue", 
            "minValue": 3,
            "maxValue": 7,
            "unitCode": "DAY"
          }
        }
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.7",
      "reviewCount": "23",
      "bestRating": "5",
      "worstRating": "1"
    }
  }),

  // Collection/Category page schema
  collection: (collection: {
    name: string;
    description?: string;
    image?: string;
    itemCount?: number;
  }): MobileOptimizedStructuredData => ({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": collection.name,
    "description": collection.description || `Explore our curated ${collection.name.toLowerCase()} collection`,
    "image": collection.image,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": collection.itemCount || 50,
      "itemListElement": []
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://bohostyleco.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Collections",
          "item": "https://bohostyleco.com/collections"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": collection.name,
          "item": `https://bohostyleco.com/collections/${collection.name.toLowerCase().replace(/\s+/g, '-')}`
        }
      ]
    }
  }),

  // FAQ schema for better mobile search results
  faq: (faqs: Array<{ question: string; answer: string }>): MobileOptimizedStructuredData => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  }),

  // Local business schema for mobile location searches
  localBusiness: (): MobileOptimizedStructuredData => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Boho Style Co.",
    "description": "Online bohemian fashion and lifestyle boutique",
    "url": "https://bohostyleco.com",
    "telephone": "+1-555-BOHO-STYLE",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "paymentAccepted": "Credit Card, PayPal, Apple Pay, Google Pay",
    "currenciesAccepted": "USD"
  }),

  // Website schema with mobile app information
  website: (): MobileOptimizedStructuredData => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Boho Style Co.",
    "url": "https://bohostyleco.com",
    "description": "Handcrafted bohemian fashion, jewelry, and lifestyle accessories",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://bohostyleco.com/products?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Boho Style Co.",
      "logo": "https://bohostyleco.com/lovable-uploads/0a39b4fd-debb-4ee4-9c46-86c84d718d61.png"
    }
  })
};

interface CombinedMobileSchemaData {
  id?: string;
  name?: string;
  description?: string;
  image?: string;
  price?: number;
  availability?: boolean;
  category?: string;
  brand?: string;
  itemCount?: number;
  question?: string;
  answer?: string;
}

// Combine multiple schemas for rich mobile search results
export const generateCombinedMobileSchema = (
  pageType: 'home' | 'product' | 'collection' | 'about' | 'article',
  data?: CombinedMobileSchemaData
): MobileOptimizedStructuredData[] => {
  const schemas = [
    generateMobileStructuredData.organization(),
    generateMobileStructuredData.website()
  ];

  switch (pageType) {
    case 'home':
      schemas.push(generateMobileStructuredData.localBusiness());
      break;
    case 'product':
      if (data && data.id && data.name && typeof data.price === 'number') {
        schemas.push(generateMobileStructuredData.product({
          id: data.id,
          name: data.name,
          description: data.description,
          image: data.image,
          price: data.price,
          availability: data.availability,
          category: data.category,
          brand: data.brand
        }));
      }
      break;
    case 'collection':
      if (data && data.name) {
        schemas.push(generateMobileStructuredData.collection({
          name: data.name,
          description: data.description,
          image: data.image,
          itemCount: data.itemCount
        }));
      }
      break;
    case 'about':
      schemas.push(generateMobileStructuredData.localBusiness());
      break;
    case 'article':
      // Article structured data is handled separately in the blog component
      break;
  }

  return schemas;
};